import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { toastr } from "react-redux-toastr"

import { updateDesignRoomPosition } from '../../../../api/lt-api';

class DesignRoomPositionInput extends Component {
  static propTypes = {
    room: PropTypes.object.isRequired,
    rooms: PropTypes.array.isRequired,
    room_variant_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    onPositionUpdate: PropTypes.func, // Callback when position is updated
  };

  static defaultProps = {
    onPositionUpdate: null,
  };

  state = {
    position: this.props.room.position || '',
    isLoading: false,
    error: null,
  };

  componentDidUpdate(prevProps) {
    if (prevProps.room.position !== this.props.room.position) {
      this.setState({ position: this.props.room.position || '' });
    }
    // Sync rooms when they change in props
    if (prevProps.rooms !== this.props.rooms) {
      this.forceUpdate();
    }
  }

  handleChange = (event) => {
    const newPosition = event.target.value;
    this.setState({ position: newPosition });

    if (newPosition === '' || parseInt(newPosition, 10) >= 1) {
      this.updatePosition(newPosition);
    }
  };

  updatePosition = (query) => {
    const { room_variant_id, room, onPositionUpdate } = this.props;
    const toasterSuccess = "Success, the design room position successfully updated";
    const toasterError = "Error, the design room position wasn't updated";
    const toasterWarning = "Warning, the design room position wasn't updated";

    if (query.length > 0 && !isNaN(parseInt(query, 10))) {
      const newPosition = Number.parseInt(query, 10);
      this.setState({ isLoading: true, error: null });

      updateDesignRoomPosition(room.design_room_id, room_variant_id, query)
        .then((response) => {
          const { result, rooms: responseRooms, warning } = response || {};

          if (result) {
            this.setState({ isLoading: false });
            toastr.success(toasterSuccess);

            // Invoke callback so parent updates data
            if (onPositionUpdate) {
              if (Array.isArray(responseRooms)) {
                const mergedRooms = this.mergeRoomPositions(responseRooms);
                onPositionUpdate({
                  design_room_id: room.design_room_id,
                  new_position: newPosition,
                  rooms: mergedRooms,
                });
              } else {
                this.setState({ error: "Backend didn't return rooms array" });
                toastr.warning("Backend didn't return rooms array");
              }
            }
          } else {
            this.setState({ isLoading: false, error: warning || toasterWarning });
            toastr.warning(warning || toasterWarning);
            this.setState({ position: this.props.room.position || '' });
          }
        })
        .catch((error) => {
          this.setState({ isLoading: false, error: error.message });
          toastr.error(toasterError);
          this.setState({ position: this.props.room.position || '' });
        });
    }
  };

  // Merge backend positions into current room objects
  mergeRoomPositions = (responseRooms) => {
    const { rooms } = this.props;
    const positionById = responseRooms.reduce((acc, r) => {
      acc[r.design_room_id] = r.position;
      return acc;
    }, {});

    return rooms.map((r) => (
      positionById[r.design_room_id] !== undefined
        ? { ...r, position: positionById[r.design_room_id] }
        : r
    ));
  };

  render() {
    const { position, isLoading, error } = this.state;
    const { rooms, room } = this.props;
    const positions = rooms.map(r => r.position);
    const hasNullPosition = positions.some(p => p === null || p === undefined);
    const validPositions = positions.filter(p => p !== null && p !== undefined).sort((a, b) => a - b);
    const isConsecutive = !hasNullPosition && validPositions.length === rooms.length &&
      validPositions.every((p, i) => p === i + 1 && positions.indexOf(p) === i);

    const positionToIndex = {};
    if (!isConsecutive && !hasNullPosition) {
      positions.forEach((pos, idx) => {
        if (pos !== null && pos !== undefined) {
          positionToIndex[idx + 1] = pos;
        }
      });
    }

    const currentRoomIndex = rooms.findIndex(r => r.design_room_id === room.design_room_id);
    const selectValue = hasNullPosition
      ? (currentRoomIndex >= 0 ? String(currentRoomIndex + 1) : '')
      : isConsecutive
        ? position
        : (currentRoomIndex >= 0 && positionToIndex[currentRoomIndex + 1] === position ? String(currentRoomIndex + 1) : '');

    return (
      <div className="position-input-wrapper" style={{ marginTop: '20px' }}>
        <p className="room-row-title" style={{ marginBottom: '10px' }}>Position</p>
        <select
          value={selectValue}
          onChange={this.handleChange}
          disabled={isLoading}
          className="form-control position-input"
        >
          {hasNullPosition || !isConsecutive ? (
            Array.from({ length: rooms.length }, (_, i) => i + 1).map(p => (
              <option className="frontend" key={p} value={p}>
                {p}
              </option>
            ))
          ) : (
            rooms.map((r, idx) => (
              <option className="backend" key={r.design_room_id} value={r.position}>
                {r.position}
              </option>
            ))
          )}
        </select>
        {isLoading && <small style={{ marginTop: '5px', display: 'block' }}>Updating...</small>}
        {error && <small style={{ marginTop: '5px', display: 'block', color: '#c00' }}>{error}</small>}
      </div>
    );
  }
}

export default DesignRoomPositionInput;
